import {
  fieldSchemaError,
  validateFieldValue,
  type FieldDefinition,
  type FieldState,
  type TypedFieldValue,
} from '@/components/objects/typedFieldData';
import type { FieldRegistry } from '@/components/objects/fieldRegistry';
export type ObjectFormValues = Readonly<Record<string, TypedFieldValue>>;
export interface ObjectFormSection {
  id: string;
  title: string;
  description?: string;
  fieldIds: readonly string[];
}
export interface ObjectFormDraft {
  values: ObjectFormValues;
  step: number;
}
export type ObjectFormErrors = Readonly<Record<string, string>>;

export function formSchemaError(
  definitions: readonly FieldDefinition[],
  sections: readonly ObjectFormSection[],
): string | null {
  const schemaError = fieldSchemaError(definitions);
  if (schemaError) return schemaError;
  if (!sections.length) return 'Добавь хотя бы один раздел формы.';
  const ids = new Set<string>(),
    fields = new Set<string>();
  for (const section of sections) {
    if (!section.id.trim() || !section.title.trim() || ids.has(section.id))
      return 'Разделы должны иметь разные id и названия.';
    ids.add(section.id);
    for (const id of section.fieldIds) {
      if (!definitions.some((d) => d.id === id) || fields.has(id))
        return 'Каждое поле должно входить ровно в один раздел.';
      fields.add(id);
    }
  }
  return fields.size === definitions.length ? null : 'Каждое поле должно входить в раздел.';
}
export function emptyFormValues(definitions: readonly FieldDefinition[]): ObjectFormValues {
  return Object.fromEntries(definitions.map((d) => [d.id, { type: d.type, value: null }])) as Record<
    string,
    TypedFieldValue
  >;
}
export function formStates(values: ObjectFormValues): Record<string, FieldState> {
  return Object.fromEntries(Object.entries(values).map(([id, value]) => [id, { state: 'ready', value }]));
}
export function validateForm(
  definitions: readonly FieldDefinition[],
  values: ObjectFormValues,
  registry: FieldRegistry = {},
): ObjectFormErrors {
  const result: Record<string, string> = {};
  for (const definition of definitions) {
    const value = values[definition.id] ?? ({ type: definition.type, value: null } as TypedFieldValue);
    const issue = validateFieldValue(definition, value);
    if (issue) result[definition.id] = issue;
    else if (definition.type.startsWith('custom:') && value.value !== null) {
      const adapter = registry[definition.type];
      if (!adapter) result[definition.id] = 'Редактор этого поля не подключён.';
      else {
        const error = adapter.validate(value.value as never, definition as never);
        if (error) result[definition.id] = error;
      }
    }
  }
  return result;
}
/** Drafts may be incomplete but never malformed. Unknown fields/secret plaintext are rejected. */
export function decodeFormDraft(
  input: unknown,
  definitions: readonly FieldDefinition[],
  sectionCount: number,
): ObjectFormDraft {
  if (!input || typeof input !== 'object' || Array.isArray(input)) throw new Error('Invalid form draft');
  const record = input as { values?: unknown; step?: unknown };
  if (
    !record.values ||
    typeof record.values !== 'object' ||
    Array.isArray(record.values) ||
    !Number.isInteger(record.step) ||
    (record.step as number) < 0 ||
    (record.step as number) > sectionCount
  )
    throw new Error('Invalid form draft');
  if (Object.keys(input).some((key) => key !== 'values' && key !== 'step'))
    throw new Error('Unexpected draft field');
  const source = record.values as Record<string, unknown>;
  if (Object.keys(source).some((id) => !definitions.some((d) => d.id === id)))
    throw new Error('Unknown field');
  const values: Record<string, TypedFieldValue> = {};
  for (const definition of definitions) {
    const value = Object.hasOwn(source, definition.id)
      ? source[definition.id]
      : { type: definition.type, value: null };
    if (
      !value ||
      typeof value !== 'object' ||
      Array.isArray(value) ||
      !Object.hasOwn(value, 'type') ||
      !Object.hasOwn(value, 'value') ||
      Object.keys(value).some((key) => key !== 'type' && key !== 'value')
    )
      throw new Error('Invalid field envelope');
    if (validateFieldValue({ ...definition, required: false } as FieldDefinition, value))
      throw new Error('Invalid field draft');
    values[definition.id] = value as TypedFieldValue;
  }
  return { values, step: record.step as number };
}
export function sameFormValues(a: ObjectFormValues, b: ObjectFormValues): boolean {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  return [...keys].every((key) => JSON.stringify(a[key]) === JSON.stringify(b[key]));
}
export class ObjectFormSubmitError extends Error {
  readonly fields: ObjectFormErrors;
  constructor(message: string, fields: ObjectFormErrors = {}) {
    super(message);
    this.fields = fields;
  }
}
